import { AggregateRoot, UniqueEntityId } from "@kernel/ddd";
import { StreamTrackService } from "./services";
import { cloneDeep } from "lodash";

export type StreamSourceType = "camera" | "screen";

export interface StreamProps {
  mediaStream: MediaStream;
  local: boolean;
  sourceType: StreamSourceType;
}

export class Stream extends AggregateRoot<StreamProps> {
  public get mediaStream(): MediaStream {
    return this.props.mediaStream;
  }

  public getTracks(): MediaStreamTrack[] {
    return this.mediaStream.getTracks();
  }

  public hasTrack(type: "audio" | "video") {
    return this.getTracks().some((t) => t.kind === type);
  }

  public isEmpty(): boolean {
    return this.getTracks().length === 0;
  }

  public isFullfilled(): boolean {
    return !this.isEmpty();
  }

  public hasEnabledTrack(type: "audio" | "video") {
    if (!this.isLocal()) return true;
    const track = this.getTracks().find((t) => t.kind === type);
    if (!track) return false;
    return track.enabled;
  }

  public restart(type: "audio" | "video") {
    if (!this.isLocal()) return;
    const service = new StreamTrackService(this);
    service.restart(type);
  }

  public start(element: HTMLVideoElement) {
    if (!this.isLocal()) return;
    const service = new StreamTrackService(this);
    service.start(element);
  }

  public stop(type: "audio" | "video") {
    if (!this.isLocal()) return;
    const service = new StreamTrackService(this);
    service.stop(type);
  }

  public addTrack(track: MediaStreamTrack) {
    this.mediaStream.addTrack(track);
  }

  public isLocal(): boolean {
    return this.props.local;
  }

  public getSourceType(): StreamSourceType {
    return this.props.sourceType;
  }

  public isScreenSource(): boolean {
    return this.props.sourceType === "screen";
  }

  public clone(): Stream {
    const props = cloneDeep(this.props);
    return new Stream(props, new UniqueEntityId(this._id.toValue()));
  }

  public toString(): string {
    return this.id.toString();
  }

  public static create(props: StreamProps, id?: UniqueEntityId): Stream {
    return new Stream(props, id);
  }
}
